/**
 * ALDER SEARCH — Board Director Placement Page
 * URL: /executive-search/board-director-placement
 */

import { useEffect } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useFaqSchema } from "@/hooks/useFaqSchema";

const boardDirectorFaqs = [
  {
    question: "What is a Non-Executive Director (NED) in a VC- and PE-backed company?",
    answer: "A Non-Executive Director in a VC- and PE-backed company is an independent board member who provides strategic oversight, governance, and challenge to the executive team. In Alder's product practice, NEDs bring product, digital, or customer expertise that complements the sponsor's capabilities and supports the value creation plan.",
  },
  {
    question: "How do you recruit Non-Executive Directors for VC- and PE-backed portfolio companies?",
    answer: "We recruit NEDs through a retained search process that maps candidates with relevant sector experience, VC and PE board experience, and product expertise the board requires. NED searches require a different approach to executive searches because the candidate pool is relationship-driven and the assessment criteria include governance capability alongside operational credibility.",
  },
  {
    question: "What makes a strong NED candidate for a VC- and PE-backed business?",
    answer: "A strong NED candidate for a VC- and PE-backed business has direct experience of operating or advising businesses at a similar stage of VC and PE ownership, understands how to add value in a sponsor-backed governance structure, and brings product, digital, or customer expertise that the existing board lacks. They must be able to challenge constructively without undermining management confidence.",
  },
  {
    question: "How long does a board director search take?",
    answer: "A board director search typically takes eight to twelve weeks from brief to accepted offer. The NED market is relationship-driven and requires careful confidentiality management, particularly when the search is for a specific functional expertise that might signal a board capability gap.",
  },
  {
    question: "Can you run board director searches for Chair appointments as well as NEDs?",
    answer: "Yes. We run searches for both Chair and NED appointments. Chair searches require a different brief and candidate profile because the Chair has a distinct governance role, manages the relationship between the board and the sponsor, and often plays a significant role in CEO performance management.",
  }
];


const steps = [
  {
    num: "01",
    title: "Board Need Assessment",
    body: "We start by understanding the board's current composition and the gap. Is this a product, digital, or customer appointment? A sector credibility play? Exit preparation? The answer shapes the profile entirely.",
  },
  {
    num: "02",
    title: "Profile Definition",
    body: "We define the director profile together — the product background required, the VC and PE governance experience expected, the sector depth needed, and the commitment level the role demands. We also discuss fit with the existing board dynamic.",
  },
  {
    num: "03",
    title: "Candidate Universe",
    body: "We map independent directors with verified VC and PE board experience in your relevant product and sector areas. We look beyond the obvious names and beyond the standard NED databases. The best directors are rarely available — they're in our network.",
  },
  {
    num: "04",
    title: "Fit Assessment",
    body: "We assess every candidate for functional credibility, board-level conduct, and genuine independence. We look for directors who'll challenge the management team constructively — not ones who'll simply endorse the sponsor's view.",
  },
  {
    num: "05",
    title: "Shortlist & Reference",
    body: "A focused shortlist with written profiles. We conduct structured reference calls with people who've sat on boards with these candidates — not character references.",
  },
  {
    num: "06",
    title: "Placement & Governance Integration",
    body: "We support the director through the appointment process and, where needed, provide context on the specific governance dynamics of the portfolio company and sponsor relationship.",
  },
];

const outcomes = [
  "A product-focused director assessed against your governance needs and the specific stage of ownership — not a standard NED profile.",
  "Access to a curated network of independent directors with verified VC and PE board experience in product and your sector.",
  "A placement that contributes from day one — not one that takes six months to understand the business.",
];

export default function BoardDirectorPage() {
  useFaqSchema(boardDirectorFaqs, "board-faq-jsonld");
  useEffect(() => {
    const prev = document.title;
    document.title = "Product Board Director Placement for VC- and PE-Backed Companies | Alder Search";
    const meta = document.querySelector('meta[name="description"]');
    const prevContent = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "Independent product board director search for VC- and PE-backed businesses. We place directors with the product depth, sector credibility, and governance experience VC and PE require."
    );
    return () => {
      document.title = prev;
      meta?.setAttribute("content", prevContent);
    };
  }, []);

  const handleEngageClick = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/#contact";
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.006 80)" }}>
      <Navigation />

      {/* HERO */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{ background: "linear-gradient(160deg, oklch(0.18 0.055 155) 0%, oklch(0.245 0.065 155) 100%)" }}
      >
        <div className="container relative z-10">
          <div className="mb-4 flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <Link href="/" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Home</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <Link href="/executive-search" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Executive Search</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <span style={{ color: "oklch(0.62 0.085 75)" }}>Product Board Director Placement</span>
          </div>
          <h1 className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 1.1, color: "oklch(0.97 0.006 80)", maxWidth: "760px" }}>
            The Product Board Director Who Earns Their Seat.
          </h1>
          <p className="mb-10" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "clamp(1rem, 2vw, 1.15rem)", lineHeight: 1.75, color: "oklch(0.78 0.018 80)", maxWidth: "580px" }}>
            An independent product director placed well adds governance credibility, functional depth, and a perspective neither the sponsor nor the management team can give themselves. Placed badly, it's a wasted seat. We place the right people.
          </p>
          <div className="flex flex-wrap gap-4">
            <button onClick={handleEngageClick} className="px-8 py-3.5 transition-all duration-300" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
              Engage Alder Search
            </button>
            <Link href="/executive-search" className="px-8 py-3.5 transition-all duration-300" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.75rem", background: "transparent", color: "oklch(0.88 0.012 80)", border: "1px solid oklch(0.45 0.07 155)", borderRadius: "2px", textDecoration: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.62 0.085 75)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.45 0.07 155)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.012 80)"; }}>
              ← Back to Executive Search
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES STRIP */}
      <section className="py-14" style={{ background: "oklch(0.245 0.065 155)", borderBottom: "1px solid oklch(0.32 0.07 155)" }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((text, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="mt-1 shrink-0" style={{ width: "20px", height: "20px", borderRadius: "50%", background: "oklch(0.62 0.085 75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="oklch(0.18 0.055 155)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.65, color: "oklch(0.82 0.015 80)" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-24" style={{ background: "oklch(0.97 0.006 80)" }}>
        <div className="container">
          <div className="mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>How This Search Works</div>
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.245 0.065 155)", maxWidth: "600px" }}>
            Product Board Director Search That Goes Beyond the Obvious Names.
          </h2>
          <p className="mb-16" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)", maxWidth: "560px" }}>
            The pool of genuinely qualified independent directors with VC and PE board experience in product and your sector is smaller than it looks. We know it well.
          </p>
          <div className="flex flex-col gap-0">
            {steps.map((step) => (
              <div key={step.num} className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 py-8" style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 400, fontSize: "0.72rem", letterSpacing: "0.1em", color: "oklch(0.62 0.085 75)", paddingTop: "3px" }}>{step.num}</div>
                <div>
                  <h3 className="mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: "1.05rem", color: "oklch(0.245 0.065 155)" }}>{step.title}</h3>
                  <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)" }}>{step.body}</p>
                </div>
              </div>
            ))}
            <div style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }} />
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-20" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="container">
          <div className="mx-auto" style={{ maxWidth: "680px", textAlign: "center" }}>
            <div className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: "clamp(1.15rem, 2.5vw, 1.55rem)", lineHeight: 1.6, color: "oklch(0.88 0.012 80)" }}>
              "The right independent director brings a perspective neither the sponsor nor the management team can provide themselves."
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>
              Ryan Ollerenshaw, Founding Partner, Alder Search
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section id="contact" className="py-24" style={{ background: "oklch(0.245 0.065 155)" }}>
        <div className="container text-center">
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.97 0.006 80)" }}>
            Looking to Strengthen Your Board?
          </h2>
          <p className="mb-10 mx-auto" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.72 0.018 80)", maxWidth: "520px" }}>
            Whether you're making an appointment for governance, functional expertise, or exit preparation — we welcome a confidential conversation about what the right director looks like for your portfolio company.
          </p>
          <button onClick={handleEngageClick} className="px-10 py-4 transition-all duration-300" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
            Engage Alder Search
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
